import { useState } from "react";
import toast from "react-hot-toast";
import { X } from "lucide-react";
import { useCreateTaxObligation } from "@/api/taxes";
import { useClients, displayName } from "@/api/clients";
import { getApiErrorMessage } from "@/api/client";

const TAX_TYPE_SUGGESTIONS = ["IVA", "Retención en la fuente", "Renta", "ICA", "Autorretención"];

export default function CreateTaxObligationModal({
  clientId: fixedClientId,
  onClose,
}: {
  clientId?: string;
  onClose: () => void;
}) {
  const createTaxObligation = useCreateTaxObligation();
  const [taxType, setTaxType] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [amount, setAmount] = useState("");
  const [confirmationNumber, setConfirmationNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<{ id: string; name: string } | null>(null);

  const { data: clientResults } = useClients({ search: clientSearch, pageSize: 6 });
  const effectiveClientId = fixedClientId ?? selectedClient?.id;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!effectiveClientId) {
      toast.error("Debe seleccionar un cliente. Las obligaciones tributarias siempre están asociadas a un cliente.");
      return;
    }
    try {
      await createTaxObligation.mutateAsync({
        clientId: effectiveClientId,
        taxType,
        periodLabel,
        dueDate,
        amount: amount || undefined,
        confirmationNumber: confirmationNumber || undefined,
        notes: notes || undefined,
      });
      toast.success("Obligación tributaria creada correctamente.");
      onClose();
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
      <div className="card w-full max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Nueva obligación tributaria</h2>
          <button type="button" className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Cerrar">
            <X size={18} />
          </button>
        </div>
        <form className="mt-4 space-y-3" onSubmit={handleSubmit}>
          {!fixedClientId && (
            <div>
              <label className="block text-sm font-medium text-gray-700">Cliente</label>
              {selectedClient ? (
                <div className="mt-1 flex items-center justify-between rounded-md border border-gray-200 px-3 py-2 text-sm">
                  <span>{selectedClient.name}</span>
                  <button type="button" className="text-xs text-gray-400 hover:text-red-600" onClick={() => setSelectedClient(null)}>
                    Quitar
                  </button>
                </div>
              ) : (
                <div className="relative">
                  <input
                    className="input-field mt-1"
                    placeholder="Buscar cliente por nombre o documento..."
                    value={clientSearch}
                    onChange={(e) => setClientSearch(e.target.value)}
                    required={!selectedClient}
                  />
                  {clientSearch && clientResults?.data && clientResults.data.length > 0 && (
                    <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                      {clientResults.data.map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-50"
                          onClick={() => {
                            setSelectedClient({ id: c.id, name: displayName(c) });
                            setClientSearch("");
                          }}
                        >
                          {displayName(c)}
                          <span className="ml-1 text-xs text-gray-400">
                            {c.documentType} {c.documentNumber}
                          </span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Tipo de impuesto</label>
              <input
                className="input-field mt-1"
                list="tax-type-suggestions"
                placeholder="Ej. IVA"
                value={taxType}
                onChange={(e) => setTaxType(e.target.value)}
                required
              />
              <datalist id="tax-type-suggestions">
                {TAX_TYPE_SUGGESTIONS.map((t) => (
                  <option key={t} value={t} />
                ))}
              </datalist>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Periodo</label>
              <input
                className="input-field mt-1"
                placeholder="Ej. 2026-01"
                value={periodLabel}
                onChange={(e) => setPeriodLabel(e.target.value)}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha límite</label>
              <input type="date" className="input-field mt-1" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Valor (COP)</label>
              <input type="number" min="0" className="input-field mt-1" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">N.° de confirmación</label>
            <input
              className="input-field mt-1"
              placeholder="Número de radicado / confirmación DIAN"
              value={confirmationNumber}
              onChange={(e) => setConfirmationNumber(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Notas</label>
            <textarea className="input-field mt-1" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn-primary" disabled={createTaxObligation.isPending}>
              {createTaxObligation.isPending ? "Creando..." : "Crear obligación"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
